// japan.videolink.llyll.com — galerie privée des créations (prototypes).
// Protégée par le même login Supabase que /admin : sans session → /admin/login.
// Sert de racine quand le hostname est japan.videolink.llyll.com (cf. main.tsx),
// et reste accessible via /japan sur le domaine principal.
//
// Les prototypes + miniatures sont des fichiers statiques sous /japan/* (copiés
// verbatim depuis frontend/public/japan par Vite). Pour ajouter une création :
// déposer le fichier dans public/japan/pages, la miniature dans public/japan/thumbs,
// puis ajouter un objet en tête de ITEMS.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";

interface Item {
  title: string;
  date: string; // ISO yyyy-mm-dd
  tag: string;
  desc: string;
  href: string;
  thumb: string;
}

// La plus récente en premier (un tri de sécurité est appliqué plus bas).
const ITEMS: Item[] = [
  {
    title: "Galerie — Structures de progression",
    date: "2026-06-09",
    tag: "UI · gamification",
    desc: "6 façons de représenter une jauge de progression gamifiée (chemin Duolingo, escalier Chess, carte de niveaux, stepper, arbre de compétences, timeline).",
    href: "/japan/pages/galerie-structures-de-progression.html",
    thumb: "/japan/thumbs/galerie.png",
  },
  {
    title: "Quiz FR ↔ ES",
    date: "2026-06-07",
    tag: "Prototype",
    desc: "Mini-jeu de quiz bilingue français / espagnol, autonome et jouable.",
    href: "/japan/pages/quiz-fr-es.html",
    thumb: "/japan/thumbs/quiz.png",
  },
  {
    title: "Concept detail — LLYLL Studio",
    date: "2026-06-04",
    tag: "Mockup produit",
    desc: "Maquette de l'écran de configuration d'un Concept (Shooting Sets + Montage) pour studio.llyll.com.",
    href: "/japan/pages/concept-detail-mockup.html",
    thumb: "/japan/thumbs/concept.png",
  },
  {
    title: "Briefing — brainstorm Chief",
    date: "2026-05-18",
    tag: "Brand · brainstorm",
    desc: "Exploration de compositions « Le Chief » + insert vidéo pour les briefings LLYLL.",
    href: "/japan/pages/brief-brainstorm/index.html",
    thumb: "/japan/thumbs/brief.png",
  },
  {
    title: "Avatar Builder — Videolink",
    date: "2026-05-07",
    tag: "Mockup produit",
    desc: "Écran minimal de construction d'avatar : personnage, fond, voix, langue — en un écran.",
    href: "/japan/pages/avatar-builder-standalone.html",
    thumb: "/japan/thumbs/avatar.png",
  },
];

const FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];
function fmt(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return `${d.getDate()} ${FR[d.getMonth()]} ${d.getFullYear()}`;
}

const C = {
  ivory: "#D7D2C9", stone: "#8E8A82", ash: "#B5B0A6", concrete: "#5A5650",
  charcoal2: "#262420", black: "#141311", lemon: "#FFFC7D", line: "#322f2a",
  grot: '"Space Grotesk", Arial, Helvetica, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, Menlo, monospace',
};

const STYLE = `
.cre-card{transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease}
.cre-card:hover{transform:translateY(-4px);border-color:${C.ash};box-shadow:0 18px 40px rgba(0,0,0,.45)}
.cre-card:hover img{transform:scale(1.04)}
.cre-card img{transition:transform .4s ease}
.cre-card:hover .cre-open::after{transform:translateX(4px)}
.cre-open::after{content:"→";transition:transform .18s ease;display:inline-block;margin-left:7px}
`;

export default function Creations() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);

  // Même garde que Admin.tsx : pas de session Supabase → /admin/login.
  useEffect(() => {
    // Send unauthenticated users to the login, remembering the current path so
    // they land back on THIS gallery afterwards (not on /admin).
    const loginPath = `/admin/login?next=${encodeURIComponent(
      window.location.pathname
    )}`;
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav(loginPath, { replace: true });
      else setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) nav(loginPath, { replace: true });
      else setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, [nav]);

  if (!ready) {
    return (
      <div style={{ minHeight: "100vh", background: C.black, color: C.stone,
        display: "grid", placeItems: "center", fontFamily: C.mono, fontSize: 13 }}>
        Vérification de l'accès…
      </div>
    );
  }

  const items = [...ITEMS].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div style={{
      minHeight: "100vh", background: C.black, color: C.ivory,
      fontFamily: C.grot, letterSpacing: "-0.01em", padding: "0 22px 90px",
      backgroundImage:
        "radial-gradient(900px 460px at 88% -8%, #2a2820 0, transparent 60%)," +
        "radial-gradient(700px 420px at -8% 12%, #23211b 0, transparent 55%)",
    }}>
      <style>{STYLE}</style>

      <header style={{ maxWidth: 1180, margin: "0 auto", padding: "46px 6px 26px" }}>
        <div style={{ font: `600 11px/1.4 ${C.mono}`, letterSpacing: "0.16em",
          textTransform: "uppercase", color: C.ash }}>LLYLL · atelier</div>
        <h1 style={{ fontWeight: 600, fontSize: "clamp(30px,5vw,48px)",
          letterSpacing: "-0.045em", margin: "8px 0 6px" }}>
          Mes créations <span style={{ color: C.lemon }}>·</span>
        </h1>
        <div style={{ color: C.stone, fontSize: 15, maxWidth: 640 }}>
          Toutes les maquettes et prototypes produits, du plus récent au plus ancien.
          Clique sur une vignette pour ouvrir le rendu en grand.
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 14, flexWrap: "wrap",
          font: `600 11px/1 ${C.mono}`, letterSpacing: "0.05em",
          textTransform: "uppercase", color: C.ash }}>
          <span><b style={{ color: C.lemon, fontWeight: 600 }}>{items.length}</b> créations</span>
          <span>maj <b style={{ color: C.lemon, fontWeight: 600 }}>{fmt(items[0].date)}</b></span>
          <span>accès privé</span>
          <button onClick={() => { supabase.auth.signOut(); }}
            style={{ background: "transparent", border: `1px solid ${C.line}`,
              color: C.ash, font: `600 10px/1 ${C.mono}`, letterSpacing: "0.06em",
              textTransform: "uppercase", padding: "5px 9px", borderRadius: 6, cursor: "pointer" }}>
            Déconnexion
          </button>
        </div>
      </header>
      <hr style={{ maxWidth: 1180, margin: "0 auto", border: 0, borderTop: `1px solid ${C.line}` }} />

      <main style={{ maxWidth: 1180, margin: "30px auto 0", display: "grid", gap: 22,
        gridTemplateColumns: "repeat(auto-fill,minmax(310px,1fr))" }}>
        {items.map((it, i) => (
          <a key={it.href} className="cre-card" href={it.href}
            target="_blank" rel="noreferrer"
            style={{ display: "flex", flexDirection: "column", textDecoration: "none",
              color: "inherit", background: C.charcoal2, border: `1px solid ${C.line}`,
              borderRadius: 16, overflow: "hidden" }}>
            <div style={{ position: "relative", aspectRatio: "16/10", overflow: "hidden",
              background: "#0d0c0b", borderBottom: `1px solid ${C.line}` }}>
              <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2,
                font: `600 11px/1 ${C.mono}`, color: C.black, background: C.lemon,
                padding: "5px 8px", borderRadius: 7 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              {i === 0 && (
                <span style={{ position: "absolute", top: 10, right: 10, zIndex: 2,
                  font: `600 10px/1 ${C.mono}`, letterSpacing: "0.08em", textTransform: "uppercase",
                  color: C.black, background: C.lemon, padding: "5px 8px", borderRadius: 999 }}>
                  Nouveau
                </span>
              )}
              <img src={it.thumb} alt={`Aperçu — ${it.title}`} loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover",
                  objectPosition: "top center", display: "block" }} />
            </div>
            <div style={{ padding: "15px 16px 17px", display: "flex", flexDirection: "column",
              gap: 8, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "baseline",
                justifyContent: "space-between", gap: 10 }}>
                <span style={{ font: `600 11px/1 ${C.mono}`, letterSpacing: "0.05em",
                  color: C.ash, whiteSpace: "nowrap" }}>{fmt(it.date)}</span>
                <span style={{ font: `600 10px/1 ${C.mono}`, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: C.stone, border: `1px solid ${C.line}`,
                  padding: "4px 7px", borderRadius: 6 }}>{it.tag}</span>
              </div>
              <h2 style={{ fontWeight: 600, fontSize: 18, letterSpacing: "-0.03em", margin: 0 }}>
                {it.title}
              </h2>
              <p style={{ color: C.stone, fontSize: 13.5, lineHeight: 1.45, margin: 0 }}>
                {it.desc}
              </p>
              <span className="cre-open" style={{ marginTop: "auto", paddingTop: 6,
                font: `600 11px/1 ${C.mono}`, letterSpacing: "0.06em",
                textTransform: "uppercase", color: C.lemon }}>Ouvrir</span>
            </div>
          </a>
        ))}
      </main>

      <footer style={{ maxWidth: 1180, margin: "42px auto 0", color: C.concrete,
        font: `500 11px/1.6 ${C.mono}`, letterSpacing: "0.02em" }}>
        japan.videolink.llyll.com — galerie privée des prototypes · accès réservé (Supabase).
      </footer>
    </div>
  );
}
